import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, MapPin, Star, Users, Briefcase, ArrowRight, X, Filter, Building, Mail, Phone, ExternalLink, Copy, Linkedin, ChevronDown, ChevronUp, ArrowLeft, Grid3x3, List, ChevronRight, ChevronLeft, Maximize, Target, Map, UserSearch } from 'lucide-react';
import MapGL, { Marker, Popup, NavigationControl, FullscreenControl, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { motion, AnimatePresence } from 'framer-motion';

// Mapbox access token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiY2FtNTIxMCIsImEiOiJjbWM1NWI4MWowbjV3MmlxMnZnZXkyaGx1In0.ys_aPorU9RB7UxkiBtX0Ow';

// --- DUMMY DATA ---
const wholesalersData = [
  {
    id: 1,
    name: 'Jessica Miller',
    company: 'Retirement Solutions Group',
    specializations: ['TPA Services', 'Recordkeeper Services'],
    territory: 'Northeast',
    isPreferred: true,
    bio: 'With over 15 years of experience, Jessica is a leading expert in TPA and recordkeeping solutions. She helps advisors streamline their practice and deliver exceptional value to their clients.',
    valueProposition: 'Dedicated support model, industry-leading technology, and a commitment to advisor success. PlanSync Preferred Partner since 2021.',
    linkedin: 'https://www.linkedin.com/in/jessicamiller',
    email: 'j.miller@rsg.com',
    phone: '(212) 555-0182',
    profilePicture: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    lat: 40.7128,
    lng: -74.0060,
    city: 'New York',
    state: 'NY',
    zip: '10001'
  },
  {
    id: 2,
    name: 'David Chen',
    company: 'Capital Investments',
    specializations: ['Funds', 'Insurance'],
    territory: 'West Coast',
    isPreferred: false,
    bio: 'David specializes in identifying top-tier investment funds and insurance products that align with plan sponsor goals. His analytical approach helps advisors make data-driven decisions.',
    valueProposition: 'Access to a broad range of investment options and customized insurance solutions for plans of all sizes.',
    linkedin: 'https://www.linkedin.com/in/davidchen',
    email: 'd.chen@capitalinvest.com',
    phone: '(213) 555-0145',
    profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    lat: 34.0522,
    lng: -118.2437,
    city: 'Los Angeles',
    state: 'CA',
    zip: '90210'
  },
  {
    id: 3,
    name: 'Maria Garcia',
    company: 'PlanPerfect Administrators',
    specializations: ['TPA Services'],
    territory: 'Southeast',
    isPreferred: true,
    bio: 'Maria is a dedicated TPA specialist known for her meticulous attention to detail and deep understanding of compliance. She is a trusted partner for advisors navigating complex plan administration.',
    valueProposition: 'Proactive compliance support, transparent pricing, and personalized service. PlanSync Preferred Partner offering exclusive benefits.',
    linkedin: 'https://www.linkedin.com/in/mariagarcia',
    email: 'm.garcia@planperfect.com',
    phone: '(305) 555-0112',
    profilePicture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    lat: 25.7617,
    lng: -80.1918,
    city: 'Miami',
    state: 'FL',
    zip: '33101'
  },
    {
    id: 4,
    name: 'Christopher Lee',
    company: 'FiduciaryFirst Funds',
    specializations: ['Funds'],
    territory: 'Midwest',
    isPreferred: true,
    bio: 'Chris has a passion for investment management and works closely with advisors to build robust fund lineups. He is an expert in fiduciary best practices and market trends.',
    valueProposition: 'A curated selection of low-cost, high-performing funds with a strong fiduciary oversight process. PlanSync Preferred Partner.',
    linkedin: 'https://www.linkedin.com/in/christopherlee',
    email: 'c.lee@fiduciaryfirst.com',
    phone: '(312) 555-0199',
    profilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    lat: 41.8781,
    lng: -87.6298,
    city: 'Chicago',
    state: 'IL',
    zip: '60601'
  },
  {
    id: 5,
    name: 'Emily White',
    company: 'NextGen Recordkeeping',
    specializations: ['Recordkeeper Services'],
    territory: 'Southwest',
    isPreferred: false,
    bio: 'Emily is at the forefront of recordkeeping technology. She partners with advisors to implement efficient, user-friendly platforms that enhance participant engagement.',
    valueProposition: 'Cutting-edge technology, seamless data integration, and a mobile-first participant experience.',
    linkedin: 'https://www.linkedin.com/in/emilywhite',
    email: 'e.white@nextgenrk.com',
    phone: '(214) 555-0133',
    profilePicture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80',
    lat: 32.7767,
    lng: -96.7970,
    city: 'Dallas',
    state: 'TX',
    zip: '75201'
  },
  {
    id: 6,
    name: 'Michael Brown',
    company: 'Guardian Insurance Partners',
    specializations: ['Insurance'],
    territory: 'National',
    isPreferred: true,
    bio: 'Michael has a comprehensive knowledge of the group insurance market. He excels at structuring policies that provide maximum protection and value for plan participants.',
    valueProposition: 'Competitive quotes from top-rated carriers and dedicated claims support. A PlanSync Preferred Partner for insurance solutions.',
    linkedin: 'https://www.linkedin.com/in/michaelbrown',
    email: 'm.brown@guardianip.com',
    phone: '(800) 555-0100',
    profilePicture: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    lat: 39.8283,
    lng: -98.5795,
    city: 'National',
    state: 'USA',
    zip: '00000'
  }
];

const SPECIALIZATIONS = ['TPA Services', 'Recordkeeper Services', 'Insurance', 'Funds'];
const TERRITORIES = ['Northeast', 'Southeast', 'Midwest', 'West Coast', 'Southwest', 'National'];


// --- UTILITY & FORMATTING FUNCTIONS ---
const formatPhoneNumber = (phone) => {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

// Function to calculate distance between two coordinates in miles
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};


// --- SUB-COMPONENTS ---

const WholesalerResultCard = React.memo(({ wholesaler, onSelect, onHover, isHovered, userLocation }) => {
  // Calculate distance if user location is available
  const distance = userLocation ? calculateDistance(
    userLocation.latitude,
    userLocation.longitude,
    wholesaler.lat,
    wholesaler.lng
  ) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border transition-all duration-300 ${
        isHovered 
          ? 'border-blue-300/60 shadow-xl transform -translate-y-2 bg-white/90' 
          : 'border-slate-200/60 hover:border-slate-300/60 hover:shadow-xl hover:-translate-y-1'
      }`}
      onMouseEnter={() => onHover(wholesaler.id)}
      onMouseLeave={() => onHover(null)}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            {/* Profile Picture */}
            <div className="flex-shrink-0">
              {wholesaler.profilePicture ? (
                <img
                  src={wholesaler.profilePicture}
                  alt={wholesaler.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white text-xl font-bold shadow-lg ${
                  wholesaler.profilePicture ? 'hidden' : 'flex'
                }`}
              >
                {wholesaler.name.charAt(0)}
              </div>
            </div>
            
            {/* Name and Company */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-slate-800 line-clamp-1 mb-1">
                {wholesaler.name}
              </h3>
              <p className="text-sm text-slate-600 flex items-center">
                <Building className="w-4 h-4 mr-2 flex-shrink-0 text-indigo-500" />
                {wholesaler.company}
              </p>
            </div>
          </div>
          {wholesaler.isPreferred && (
            <div className="flex items-center px-3 py-1.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200">
              <Star className="w-3 h-3 mr-1.5 text-yellow-500 fill-current" />
              Preferred Partner
            </div>
          )}
        </div>

        <div className="flex items-center text-sm text-slate-500 mb-4 bg-slate-50/60 rounded-lg p-2">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-slate-400" />
          <span className="flex-1">{wholesaler.territory} Territory ({wholesaler.city}, {wholesaler.state})</span>
          {distance !== null && (
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-lg ml-2">
              {distance.toFixed(1)} mi
            </span>
          )}
        </div>

        <div className="mb-4">
            <p className="text-xs text-slate-500 font-semibold mb-2 flex items-center">
                <Briefcase className="w-4 h-4 mr-2" />
                Specializations
            </p>
            <div className="flex flex-wrap gap-2">
                {wholesaler.specializations.map((spec, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 border border-slate-200">
                    {spec}
                    </span>
                ))}
            </div>
        </div>
        
        <p className="text-sm text-slate-600 line-clamp-2 mb-4">
            {wholesaler.bio}
        </p>

        <button
          onClick={() => onSelect(wholesaler)}
          className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          View Profile
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </motion.div>
  );
});
WholesalerResultCard.displayName = 'WholesalerResultCard';


const WholesalerDetailView = React.memo(({ wholesaler, onClose }) => {
    
    const handleCopy = (text) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            // Optional: Show a success message
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
        document.body.removeChild(textArea);
    };

    return (
    <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 h-full w-full lg:w-2/5 bg-gray-50 z-50 shadow-2xl border-l border-gray-200 flex flex-col"
    >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
                <button onClick={onClose} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Search
                </button>
                <div className="flex items-center space-x-2">
                     <a href={wholesaler.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <Linkedin className="w-5 h-5" />
                    </a>
                    {/* <button onClick={() => handleCopy(`${wholesaler.name}, ${wholesaler.company}`)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <Copy className="w-5 h-5" />
                    </button> */}
                </div>
            </div>
        </div>

        {/* Scrollable Content */}
        <div className="h-full overflow-y-auto pb-20">
            <div className="p-6">
                {/* Profile Header */}
                <div className="text-center mb-6">
                    {wholesaler.profilePicture ? (
                        <img
                            src={wholesaler.profilePicture}
                            alt={wholesaler.name}
                            className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-white shadow-lg mb-4"
                            onError={(e) => {
                                // Fallback to initials if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div 
                        className={`w-24 h-24 rounded-full mx-auto bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white text-4xl font-bold shadow-lg mb-4 ${
                            wholesaler.profilePicture ? 'hidden' : 'flex'
                        }`}
                    >
                        {wholesaler.name.charAt(0)}
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">{wholesaler.name}</h1>
                    <p className="text-lg text-gray-600">{wholesaler.company}</p>
                    {wholesaler.isPreferred && (
                         <div className="inline-flex items-center px-3 py-1 mt-2 rounded-full text-sm font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200">
                            <Star className="w-4 h-4 mr-1.5 text-yellow-500 fill-current" />
                            PlanSync Preferred Partner
                        </div>
                    )}
                </div>

                {/* Contact Info */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                     <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
                     <div className="space-y-3">
                         <div className="flex items-center">
                             <Mail className="w-5 h-5 text-gray-400 mr-3" />
                             <a href={`mailto:${wholesaler.email}`} className="text-sm text-blue-600 hover:text-blue-700">{wholesaler.email}</a>
                         </div>
                         <div className="flex items-center">
                             <Phone className="w-5 h-5 text-gray-400 mr-3" />
                             <a href={`tel:${wholesaler.phone}`} className="text-sm text-blue-600 hover:text-blue-700">{formatPhoneNumber(wholesaler.phone)}</a>
                         </div>
                         <div className="flex items-center">
                             <Linkedin className="w-5 h-5 text-gray-400 mr-3" />
                             <a href={wholesaler.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700">View LinkedIn Profile</a>
                         </div>
                     </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                         <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                            <Briefcase className="w-5 h-5 text-indigo-500 mr-2" />
                            Specializations
                        </h3>
                        <div className="flex flex-wrap gap-2">
                             {wholesaler.specializations.map((spec, index) => (
                                <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium bg-indigo-100 text-indigo-800">
                                {spec}
                                </span>
                            ))}
                        </div>
                    </div>
                     <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                         <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                            <MapPin className="w-5 h-5 text-teal-500 mr-2" />
                            Territory
                        </h3>
                        <p className="text-sm text-gray-700">{wholesaler.territory}</p>
                        <p className="text-xs text-gray-500">{wholesaler.city}, {wholesaler.state}</p>
                    </div>
                </div>

                {/* Bio */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-2">About {wholesaler.name.split(' ')[0]}</h3>
                    <p className="text-base text-gray-600 leading-relaxed">{wholesaler.bio}</p>
                </div>

                {/* Value Proposition */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-sm border border-blue-200/50 p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Unique Value Proposition</h3>
                    <p className="text-base text-gray-700 leading-relaxed">{wholesaler.valueProposition}</p>
                </div>

            </div>
        </div>
    </motion.div>
  );
});
WholesalerDetailView.displayName = 'WholesalerDetailView';


// Fullscreen Map Component
const FullscreenMap = React.memo(({ 
  isOpen, 
  onClose, 
  mapViewState, 
  handleMapViewStateChange, 
  filteredWholesalers, 
  hoveredWholesalerId, 
  handlePopupOpen, 
  setHoveredWholesalerId, 
  popupInfo, 
  handlePopupClose,
  handleSelectWholesaler 
}) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-[95vw] h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-white/95 backdrop-blur-md border-b border-gray-200/60 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Map className="w-6 h-6 mr-2 text-blue-600" />
                Wholesaler Locations - Full View
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {filteredWholesalers.length} wholesalers displayed on map
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Fullscreen Map */}
        <div className="w-full h-full pt-20">
          <MapGL
            {...mapViewState}
            onMove={handleMapViewStateChange}
            mapboxAccessToken={MAPBOX_TOKEN}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/light-v11"
          >
            <NavigationControl position="top-right" />
            <FullscreenControl position="top-right" />
            <ScaleControl position="bottom-left" />

            {/* Wholesaler Markers */}
            {filteredWholesalers.map(w => (
              <Marker
                key={w.id}
                longitude={w.lng}
                latitude={w.lat}
                anchor="center"
              >
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transform transition-all duration-200 ${
                    hoveredWholesalerId === w.id 
                      ? 'scale-125 z-10' 
                      : 'hover:scale-110'
                  } ${
                    w.isPreferred 
                      ? 'bg-gradient-to-br from-yellow-400 to-amber-500' 
                      : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  } shadow-lg border-3 border-white`}
                  onClick={(e) => { e.stopPropagation(); handleSelectWholesaler(w); }}
                  onMouseEnter={() => {
                    setHoveredWholesalerId(w.id);
                    handlePopupOpen(w);
                  }}
                  onMouseLeave={() => {
                    setHoveredWholesalerId(null);
                    handlePopupClose();
                  }}
                >
                  {w.isPreferred ? <Star className="w-6 h-6 text-white" /> : <Briefcase className="w-6 h-6 text-white"/>}
                </div>
              </Marker>
            ))}

            {/* Popup */}
            {popupInfo && (
              <Popup
                longitude={popupInfo.lng}
                latitude={popupInfo.lat}
                anchor="bottom"
                onClose={handlePopupClose}
                closeOnClick={false}
                closeButton={false}
                className="z-20"
              >
                <div className="p-3 min-w-[280px] bg-white rounded-lg shadow-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                            {popupInfo.profilePicture ? (
                                <img
                                    src={popupInfo.profilePicture}
                                    alt={popupInfo.name}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div 
                                className={`w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white text-lg font-bold shadow-sm ${
                                    popupInfo.profilePicture ? 'hidden' : 'flex'
                                }`}
                            >
                                {popupInfo.name.charAt(0)}
                            </div>
                            <div>
                                <h3 className="font-semibold text-base text-slate-900">{popupInfo.name}</h3>
                                <p className="text-sm text-slate-600">{popupInfo.company}</p>
                            </div>
                        </div>
                        {popupInfo.isPreferred && (
                            <div className="flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200">
                                <Star className="w-3 h-3 mr-1 text-yellow-500 fill-current" />
                                Preferred
                            </div>
                        )}
                    </div>
                    
                    <div className="space-y-2 mb-3">
                        <div className="flex items-center text-sm text-slate-600">
                            <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                            <span>{popupInfo.territory} Territory • {popupInfo.city}, {popupInfo.state}</span>
                        </div>
                        <div className="flex items-center text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                            <span>{popupInfo.specializations.join(', ')}</span>
                        </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4 leading-relaxed">
                        {popupInfo.bio}
                    </p>
                    
                    {/* <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectWholesaler(popupInfo);
                        }} 
                        className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                    >
                        View Full Profile
                    </button> */}
                </div>
              </Popup>
            )}
          </MapGL>
        </div>
      </motion.div>
    </motion.div>
  );
});

FullscreenMap.displayName = 'FullscreenMap';


// --- MAIN COMPONENT ---
const WholesalerSearch = () => {
  const [wholesalers, setWholesalers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWholesaler, setSelectedWholesaler] = useState(null);
  const [hoveredWholesalerId, setHoveredWholesalerId] = useState(null);
  const [viewType, setViewType] = useState('grid');
  const [isFullscreenMap, setIsFullscreenMap] = useState(false);
  
  // User location state for distance calculations
  const [userLocation, setUserLocation] = useState(null);
  
  // Expandable filter sections state
  const [expandedSections, setExpandedSections] = useState({
    specialization: true,
    territory: true,
    location: false,
    preferred: true
  });
  
  const [filters, setFilters] = useState({
      searchTerm: '',
      specializations: [],
      territories: [],
      isPreferred: false,
      // Location filters
      region: '',
      state: '',
      city: '',
      zip: '',
      maxDistance: ''
  });

  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  const [mapViewState, setMapViewState] = useState({
    longitude: -98.5795,
    latitude: 39.8283,
    zoom: 3.5
  });
  const [popupInfo, setPopupInfo] = useState(null);
  
  useEffect(() => {
    // Simulate data fetching
    setTimeout(() => {
        setWholesalers(wholesalersData);
        setIsLoading(false);
    }, 1000);
  }, []);

  const handleFilterChange = (filterName, value) => {
      setFilters(prev => ({ ...prev, [filterName]: value }));
  };
  
  const handleCheckboxChange = (filterName, value) => {
    setFilters(prev => {
        const currentValues = prev[filterName];
        if (currentValues.includes(value)) {
            return { ...prev, [filterName]: currentValues.filter(item => item !== value) };
        } else {
            return { ...prev, [filterName]: [...currentValues, value] };
        }
    });
  };

  const clearFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      specializations: [],
      territories: [],
      isPreferred: false,
      region: '',
      state: '',
      city: '',
      zip: '',
      maxDistance: ''
    });
  }, []);

  const filteredWholesalers = useMemo(() => {
    return wholesalers.filter(w => {
        const searchTermLower = debouncedSearchTerm.toLowerCase();
        
        // Search term filter
        const matchesSearch = searchTermLower === '' ||
            w.name.toLowerCase().includes(searchTermLower) ||
            w.company.toLowerCase().includes(searchTermLower) ||
            w.city.toLowerCase().includes(searchTermLower) ||
            w.state.toLowerCase().includes(searchTermLower);

        // Specialization filter
        const matchesSpecialization = filters.specializations.length === 0 ||
            filters.specializations.some(spec => w.specializations.includes(spec));

        // Territory filter
        const matchesTerritory = filters.territories.length === 0 ||
            filters.territories.includes(w.territory);

        // Preferred partner filter
        const matchesPreferred = !filters.isPreferred || w.isPreferred;

        // Location filters
        const matchesRegion = !filters.region || w.territory.toLowerCase().includes(filters.region.toLowerCase());
        const matchesState = !filters.state || w.state.toLowerCase().includes(filters.state.toLowerCase());
        const matchesCity = !filters.city || w.city.toLowerCase().includes(filters.city.toLowerCase());
        const matchesZip = !filters.zip || w.zip?.toString().includes(filters.zip);

        // Distance filter
        let matchesDistance = true;
        if (filters.maxDistance && userLocation) {
            const maxDist = parseFloat(filters.maxDistance);
            if (!isNaN(maxDist)) {
                const distance = calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    w.lat,
                    w.lng
                );
                matchesDistance = distance <= maxDist;
            }
        }

        return matchesSearch && matchesSpecialization && matchesTerritory && matchesPreferred && 
               matchesRegion && matchesState && matchesCity && matchesZip && matchesDistance;
    });
  }, [wholesalers, debouncedSearchTerm, filters, userLocation]);
  
  const hasActiveFilters = useMemo(() => {
    return filters.searchTerm !== '' || filters.specializations.length > 0 || filters.territories.length > 0 || filters.isPreferred ||
           filters.region !== '' || filters.state !== '' || filters.city !== '' || filters.zip !== '' || filters.maxDistance !== '';
  }, [filters]);


  const handleSelectWholesaler = useCallback((wholesaler) => {
    setSelectedWholesaler(wholesaler);
    setPopupInfo(null);
    setIsFullscreenMap(false);
  }, []);

  const handleCloseDetailView = useCallback(() => {
    setSelectedWholesaler(null);
  }, []);
  
  const handleMapViewStateChange = useCallback((viewState) => {
    setMapViewState(viewState.viewState);
  }, []);

  const handlePopupClose = useCallback(() => {
    setPopupInfo(null);
  }, []);

  const handlePopupOpen = useCallback((wholesaler) => {
    setPopupInfo(wholesaler);
  }, []);

  const handleOpenFullscreenMap = useCallback(() => {
    setIsFullscreenMap(true);
  }, []);

  const handleCloseFullscreenMap = useCallback(() => {
    setIsFullscreenMap(false);
  }, []);

  // Toggle filter section expansion
  const toggleFilterSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Find wholesalers near user's location
  const handleFindWholesalersNearMe = useCallback(() => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Store user location for distance calculations
        setUserLocation({ latitude, longitude });
        
        // Update map view to user's location
        setMapViewState({
          latitude,
          longitude,
          zoom: 10 // Zoom in closer to show nearby wholesalers
        });
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to retrieve your location. Please check your browser permissions.");
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  }, []);

  if (isLoading) {
      return <div className="flex items-center justify-center h-screen bg-gray-50"><p>Loading Wholesalers...</p></div>
  }

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col overflow-hidden">
        <AnimatePresence>
            {selectedWholesaler && (
                <WholesalerDetailView wholesaler={selectedWholesaler} onClose={handleCloseDetailView} />
            )}
        </AnimatePresence>

        <div className="flex-1 flex overflow-hidden">
            {/* Left Column - Filters & Map */}
            <aside className="w-1/3 h-full flex flex-col p-6 space-y-6 overflow-y-auto scrollbar-thin">
                {/* Map */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden flex-shrink-0">
                    <div className="p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-slate-800 flex items-center">
                                <Map className="w-5 h-5 mr-2 text-blue-600" />
                                Wholesaler Locations
                            </h3>
                            <button
                                onClick={handleOpenFullscreenMap}
                                className="p-1.5 rounded-lg transition-all duration-200 text-slate-600 hover:bg-slate-100 hover:text-blue-600"
                                title="Expand map to fullscreen"
                            >
                                <Maximize className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="h-80">
                        <MapGL
                            {...mapViewState}
                            onMove={handleMapViewStateChange}
                            mapboxAccessToken={MAPBOX_TOKEN}
                            style={{ width: '100%', height: '100%' }}
                            mapStyle="mapbox://styles/mapbox/light-v11"
                        >
                             <NavigationControl position="top-right" />
                             <ScaleControl position="bottom-left" />
                             
                             {/* User Location Marker */}
                             {userLocation && (
                                 <Marker
                                     longitude={userLocation.longitude}
                                     latitude={userLocation.latitude}
                                     anchor="center"
                                 >
                                     <div className="relative">
                                         {/* Pulsing ring */}
                                         <div className="absolute inset-0 w-6 h-6 bg-blue-400 rounded-full animate-ping opacity-75"></div>
                                         {/* Inner dot */}
                                         <div className="relative w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-lg"></div>
                                     </div>
                                 </Marker>
                             )}
                             
                             {filteredWholesalers.map(w => (
                                 <Marker key={w.id} longitude={w.lng} latitude={w.lat} anchor="center">
                                     <div 
                                        className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transform transition-all duration-200 shadow-lg border-2 border-white ${
                                            hoveredWholesalerId === w.id ? 'scale-125 z-10' : 'hover:scale-110'
                                        } ${w.isPreferred ? 'bg-gradient-to-br from-yellow-400 to-amber-500' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}
                                        onClick={(e) => { e.stopPropagation(); handleSelectWholesaler(w); }}
                                        onMouseEnter={() => {
                                            setHoveredWholesalerId(w.id);
                                            setPopupInfo(w);
                                        }}
                                        onMouseLeave={() => {
                                            setHoveredWholesalerId(null);
                                            setPopupInfo(null);
                                        }}
                                    >
                                        {w.isPreferred ? <Star className="w-5 h-5 text-white" /> : <Briefcase className="w-5 h-5 text-white"/>}
                                     </div>
                                 </Marker>
                             ))}
                             {popupInfo && (
                                 <Popup
                                    longitude={popupInfo.lng}
                                    latitude={popupInfo.lat}
                                    anchor="bottom"
                                    onClose={handlePopupClose}
                                    closeOnClick={false}
                                    closeButton={false}
                                    className="z-20"
                                 >
                                    <div className="p-3 min-w-[280px] bg-white rounded-lg shadow-lg border border-gray-200">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                {popupInfo.profilePicture ? (
                                                    <img
                                                        src={popupInfo.profilePicture}
                                                        alt={popupInfo.name}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className={`w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white text-lg font-bold shadow-sm ${
                                                        popupInfo.profilePicture ? 'hidden' : 'flex'
                                                    }`}
                                                >
                                                    {popupInfo.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-base text-slate-900">{popupInfo.name}</h3>
                                                    <p className="text-sm text-slate-600">{popupInfo.company}</p>
                                                </div>
                                            </div>
                                            {popupInfo.isPreferred && (
                                                <div className="flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200">
                                                    <Star className="w-3 h-3 mr-1 text-yellow-500 fill-current" />
                                                    Preferred
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="space-y-2 mb-3">
                                            <div className="flex items-center text-sm text-slate-600">
                                                <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                                                <span>{popupInfo.territory} Territory • {popupInfo.city}, {popupInfo.state}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-slate-600">
                                                <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                                                <span>{popupInfo.specializations.join(', ')}</span>
                                            </div>
                                        </div>
                                        
                                        <p className="text-sm text-slate-600 line-clamp-2 mb-4 leading-relaxed">
                                            {popupInfo.bio}
                                        </p>
                                        
                                        {/* <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectWholesaler(popupInfo);
                                            }} 
                                            className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                                        >
                                            View Full Profile
                                        </button> */}
                                    </div>
                                 </Popup>
                             )}
                        </MapGL>
                    </div>
                 </div>

                {/* Search & Filters */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/60 p-6 flex-1 overflow-y-auto">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">Find a Wholesaler</h2>
                    
                    {/* Search Input */}
                    <div className="relative mb-6">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Name, company, location..."
                            value={filters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    {/* Filter Sections */}
                    <div className="space-y-4">
                        {/* Specialization Section */}
                        <div>
                            <button
                                onClick={() => toggleFilterSection('specialization')}
                                className="w-full flex items-center justify-between text-left group hover:bg-slate-50 -mx-2 px-2 py-2 rounded-xl transition-all duration-200"
                            >
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                    <span className="font-semibold text-slate-700 group-hover:text-blue-700">Specialization</span>
                                </div>
                                <div className={`transform transition-transform duration-200 ${expandedSections.specialization ? 'rotate-180' : ''}`}>
                                    <ChevronDown className="w-5 h-5 text-slate-500 group-hover:text-blue-600" />
                                </div>
                            </button>

                            <AnimatePresence>
                                {expandedSections.specialization && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="mt-3 overflow-hidden"
                                    >
                                        <div className="grid grid-cols-2 gap-2">
                                            {SPECIALIZATIONS.map(spec => (
                                                <label key={spec} className="flex items-center space-x-2 cursor-pointer text-sm p-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-lg transition-all duration-200 group border border-transparent hover:border-blue-200/50">
                                                    <input type="checkbox" checked={filters.specializations.includes(spec)} onChange={() => handleCheckboxChange('specializations', spec)} className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"/>
                                                    <span className="group-hover:text-blue-800">{spec}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Territory Section */}
                        <div>
                            <button
                                onClick={() => toggleFilterSection('territory')}
                                className="w-full flex items-center justify-between text-left group hover:bg-slate-50 -mx-2 px-2 py-2 rounded-xl transition-all duration-200"
                            >
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                                    <span className="font-semibold text-slate-700 group-hover:text-purple-700">Territory</span>
                                </div>
                                <div className={`transform transition-transform duration-200 ${expandedSections.territory ? 'rotate-180' : ''}`}>
                                    <ChevronDown className="w-5 h-5 text-slate-500 group-hover:text-purple-600" />
                                </div>
                            </button>

                            <AnimatePresence>
                                {expandedSections.territory && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="mt-3 overflow-hidden"
                                    >
                                        <div className="grid grid-cols-2 gap-2">
                                            {TERRITORIES.map(terr => (
                                                <label key={terr} className="flex items-center space-x-2 cursor-pointer text-sm p-2 hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 rounded-lg transition-all duration-200 group border border-transparent hover:border-purple-200/50">
                                                    <input type="checkbox" checked={filters.territories.includes(terr)} onChange={() => handleCheckboxChange('territories', terr)} className="h-4 w-4 rounded text-purple-600 focus:ring-purple-500"/>
                                                    <span className="group-hover:text-purple-800">{terr}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Preferred Partners Section */}
                        <div>
                            <button
                                onClick={() => toggleFilterSection('preferred')}
                                className="w-full flex items-center justify-between text-left group hover:bg-slate-50 -mx-2 px-2 py-2 rounded-xl transition-all duration-200"
                            >
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                                    <span className="font-semibold text-slate-700 group-hover:text-yellow-700">Preferred Partners</span>
                                </div>
                                <div className={`transform transition-transform duration-200 ${expandedSections.preferred ? 'rotate-180' : ''}`}>
                                    <ChevronDown className="w-5 h-5 text-slate-500 group-hover:text-yellow-600" />
                                </div>
                            </button>

                            <AnimatePresence>
                                {expandedSections.preferred && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="mt-3 overflow-hidden"
                                    >
                                        <label className="flex items-center space-x-2 cursor-pointer text-sm font-semibold p-2 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50 rounded-lg transition-all duration-200 group border border-transparent hover:border-yellow-200/50">
                                            <input type="checkbox" checked={filters.isPreferred} onChange={(e) => handleFilterChange('isPreferred', e.target.checked)} className="h-4 w-4 rounded text-yellow-500 focus:ring-yellow-400"/>
                                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                            <span className="group-hover:text-yellow-800">Preferred Partners Only</span>
                                        </label>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Location Filters */}
                        <div className="border-t border-slate-200 pt-4">
                            <button
                                onClick={() => toggleFilterSection('location')}
                                className="w-full flex items-center justify-between text-left group hover:bg-slate-50 -mx-2 px-2 py-2 rounded-xl transition-all duration-200"
                            >
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                                    <span className="font-semibold text-slate-700 group-hover:text-green-700">Location Filters</span>
                                </div>
                                <div className={`transform transition-transform duration-200 ${expandedSections.location ? 'rotate-180' : ''}`}>
                                    <ChevronDown className="w-5 h-5 text-slate-500 group-hover:text-green-600" />
                                </div>
                            </button>

                            <AnimatePresence>
                                {expandedSections.location && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="mt-3 overflow-hidden"
                                    >
                                        <div className="space-y-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200/50">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-1">Region/Territory</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Northeast"
                                                        value={filters.region}
                                                        onChange={(e) => handleFilterChange('region', e.target.value)}
                                                        className="w-full px-3 py-2 text-sm bg-white border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. NY, CA"
                                                        value={filters.state}
                                                        onChange={(e) => handleFilterChange('state', e.target.value)}
                                                        className="w-full px-3 py-2 text-sm bg-white border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. New York"
                                                        value={filters.city}
                                                        onChange={(e) => handleFilterChange('city', e.target.value)}
                                                        className="w-full px-3 py-2 text-sm bg-white border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-1">ZIP Code</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. 10001"
                                                        value={filters.zip}
                                                        onChange={(e) => handleFilterChange('zip', e.target.value)}
                                                        className="w-full px-3 py-2 text-sm bg-white border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                                    Max Distance {userLocation ? '(from your location)' : '(find location first)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    placeholder={userLocation ? "Enter miles..." : "Find location first"}
                                                    value={filters.maxDistance}
                                                    onChange={(e) => handleFilterChange('maxDistance', e.target.value)}
                                                    disabled={!userLocation}
                                                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                                                        !userLocation 
                                                            ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50' 
                                                            : 'border-green-200 bg-white focus:ring-green-500'
                                                    }`}
                                                    min="0"
                                                    step="1"
                                                />
                                                {!userLocation && (
                                                    <p className="text-xs text-slate-500 mt-1">
                                                        Use "Find Wholesalers Near Me" to enable distance filtering
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Find Near Me Button */}
                        <div className="border-t border-slate-200 pt-4">
                            <button 
                                onClick={handleFindWholesalersNearMe}
                                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 via-green-700 to-emerald-700 text-white font-semibold rounded-xl hover:from-green-700 hover:via-green-800 hover:to-emerald-800 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <div className="flex items-center justify-center">
                                    <MapPin className="w-5 h-5 mr-2" />
                                    Find Wholesalers Near Me
                                </div>
                            </button>
                        </div>
                    </div>
                     {hasActiveFilters && (
                        <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-800 mt-4 flex items-center">
                            <X className="w-4 h-4 mr-1"/> Clear All Filters
                        </button>
                    )}
                </div>
            </aside>
            
            {/* Right Column - Results */}
            <main className="w-2/3 h-full flex flex-col p-6 pl-0">
                 <div className="flex-1 overflow-y-auto scrollbar-thin">
                     {/* Results Header */}
                    <div className="mb-6 bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-slate-200/60 shadow-sm flex items-center justify-between">
                         <h2 className="text-xl font-bold text-slate-800">
                             {filteredWholesalers.length} Wholesalers Found
                         </h2>
                        <div className="flex items-center bg-slate-100 rounded-lg p-1">
                            <button onClick={() => setViewType('grid')} className={`p-2 rounded-lg transition-all duration-200 ${viewType === 'grid' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:bg-slate-200'}`} title="Grid View">
                                <Grid3x3 className="w-4 h-4" />
                            </button>
                            <button onClick={() => setViewType('list')} className={`p-2 rounded-lg transition-all duration-200 ${viewType === 'list' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:bg-slate-200'}`} title="List View">
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    
                    {/* Results Grid/List */}
                    {filteredWholesalers.length > 0 ? (
                        <AnimatePresence>
                             <div className={viewType === 'grid' ? "grid grid-cols-1 xl:grid-cols-2 gap-6" : "space-y-4"}>
                                {filteredWholesalers.map(wholesaler => (
                                    <WholesalerResultCard 
                                        key={wholesaler.id} 
                                        wholesaler={wholesaler} 
                                        onSelect={handleSelectWholesaler}
                                        onHover={setHoveredWholesalerId}
                                        isHovered={hoveredWholesalerId === wholesaler.id}
                                        userLocation={userLocation}
                                    />
                                ))}
                            </div>
                        </AnimatePresence>
                    ) : (
                         <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60 p-12 text-center">
                            <div className="max-w-sm mx-auto">
                                <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 mb-2">No Wholesalers Found</h3>
                                <p className="text-slate-500 mb-6">Try adjusting your filters to find more results.</p>
                                <button
                                onClick={clearFilters}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                >
                                Clear all filters
                                </button>
                            </div>
                        </div>
                    )}
                 </div>
            </main>
        </div>
        
        {/* Fullscreen Map */}
        <AnimatePresence>
            <FullscreenMap 
                isOpen={isFullscreenMap}
                onClose={handleCloseFullscreenMap}
                mapViewState={mapViewState}
                handleMapViewStateChange={handleMapViewStateChange}
                filteredWholesalers={filteredWholesalers}
                hoveredWholesalerId={hoveredWholesalerId}
                handlePopupOpen={handlePopupOpen}
                setHoveredWholesalerId={setHoveredWholesalerId}
                popupInfo={popupInfo}
                handlePopupClose={handlePopupClose}
                handleSelectWholesaler={handleSelectWholesaler}
            />
        </AnimatePresence>
    </div>
  );
};

export default WholesalerSearch;